import type { FormulaOperandRef } from '@/shared/components/Formula/formulaCompletion'
import { doubleTapProps } from '@/shared/device/doubleTap'
/**
 * Formula bar for the grids: the row between the toolbar and the column headers where a
 * programmer writes the formula of a column. Only a programmer sees it (the Grid gates it), and
 * what it edits is shared configuration in `ajustes` — see `gridFormulas.ts` and
 * docs/Grid/FORMULAS.md.
 *
 * Layout, left to right: the name box (which column is being edited, and a way to switch), the
 * cancel/accept buttons, the editor, and a button that moves the editor into a side panel for
 * long multi-line formulas. The editor itself is `FormulaEditor` (CodeMirror 6) — see that file
 * for highlighting, autocomplete, bracket matching, and signature help. Inline, Enter accepts and
 * no newline is insertable; expanded, Enter inserts a newline and Ctrl/Cmd+Enter accepts. Escape
 * cancels in both. Typing `[` opens the column list, and clicking a column header while the bar is
 * open inserts that reference — the same gestures the audience knows from Sheets.
 *
 * What the user types and reads uses VISIBLE column names and their own decimal separator; what
 * leaves this component through `onAccept` is a STORED formula — structure keyed by ids. That
 * translation is the whole reason a formula survives a header rename.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom'
import { useI18n } from '@/shared/i18n/useT'
import { useTecladoDesplegable } from './dropdownKeyboard'
import { ToolbarOverflow, type ToolbarItem } from './ToolbarOverflow'
import FormulaEditor from '@/shared/components/Formula/FormulaEditor'
import { localizedFunctionMetadata } from '@/shared/components/Formula/formulaFunctionMetadata'
import {
  parseFormula,
  serialize,
  type CellFormulaScope,
  type FormulaLocale,
  type FormulaSyntaxError,
  type FormulaWarning,
} from '@/shared/formula/language'
import { fromStored, rawFormula, toStored, type StoredFormula } from '@/shared/formula/stored'
import { SAME_COLUMN } from '@/shared/formula/goalSeek'
import type { FormulaCompletionSchema } from '@/shared/components/Formula/formulaCompletion'
import type { Translate } from '@/shared/i18n/I18nProvider'

/**
 * A parser diagnostic as text for this user's language.
 *
 * `language.ts` is deliberately free of any i18n import — it is a pure module — so it carries a
 * usable Spanish message plus, for the diagnostics worth translating, a stable `code` and the
 * values to interpolate. Anything without a `code` falls back to the message it came with, which
 * is what keeps the other ~20 parser messages working untouched.
 */
function diagnosticText(t: Translate, diagnostic: FormulaSyntaxError | FormulaWarning): string {
  switch (diagnostic.code) {
    case 'stray-dot-before-bracket':
      return t('grid.formulaWarningStrayDot', { canonical: diagnostic.data?.canonical ?? '' })
    case 'current-row-as-column':
      return t('grid.formulaWarningCurrentRowAsColumn', {
        written: diagnostic.data?.written ?? '',
        canonical: diagnostic.data?.canonical ?? '',
      })
    case 'legacy-sheet-separator':
      return t('grid.formulaErrorLegacySeparator', { suggestion: diagnostic.data?.suggestion ?? '' })
    default:
      return diagnostic.message
  }
}
import { normalizeText } from './textNormalize'

/** Last formula copied from any formula bar, so the paste button survives remounts. */
let lastCopiedFormula: string | null = null

/** A column the bar can point at. `calculated` ones can be deleted and have an editable title. */
export interface FormulaTarget {
  field: string
  name: string
  calculated: boolean
}

const BAR_HEIGHT_KEY = 'numtable_formulabar_height'
/** The one-line bar (1.75rem); dragging below it returns to one line. */
const BAR_MIN_HEIGHT = 28
const BAR_MAX_HEIGHT = 400

/** Sentinel for the "new calculated column" entry of the name box. */
export const NEW_COLUMN = '\u0000new'
/** What the bar edits: the formula of the active column, or the formula of the active cell. */
export type FormulaBarMode = 'column' | 'cell'

export interface FormulaBarProps {
  targets: FormulaTarget[]
  /** Columns available inside references. Defaults to `targets`; it may also include internal
   * calculation inputs that are intentionally absent from the target selector. */
  referenceTargets?: FormulaTarget[]
  /** Field currently being edited, or `NEW_COLUMN`. */
  selected: string
  onSelectTarget: (field: string) => void
  /** Canonical formula of the selected column; empty when creating one. */
  /** The stored formula being edited, or `null` for an empty/new one. */
  formula: StoredFormula | null
  /** Title of the selected calculated column; empty when creating one. */
  title: string
  /** Sheet-aware: a column formula may aggregate a whole column of another sheet, and the cell
   *  runtime that renders it resolves those. A plain `ColumnScope` here would parse-reject the
   *  very references the completion offers. */
  scope: CellFormulaScope
  locale: FormulaLocale
  /** Documents, sheets and columns the editor's autocomplete may offer. */
  completionSchema: FormulaCompletionSchema
  /** Receives the stored formula. `title` is only meaningful for a calculated column. */
  onAccept: (formula: StoredFormula, title: string) => void
  onDelete?: () => void
  /** A reference the Grid wants inserted (a header was clicked). The nonce makes repeats work. */
  insertRequest?: { field: string; nonce: number } | undefined
  /** Arrow-key and click pointing, forwarded to `FormulaEditor`. */
  onPointRef?: (deltaRow: number, deltaCol: number, fresh: boolean) => string | null
  pointRequest?: { ref: string; nonce: number } | undefined
  operandRefs?: readonly FormulaOperandRef[]
  expanded: boolean
  onToggleExpanded: () => void
  /** Hides the formula sidebar/bar altogether (the X next to collapse). */
  onClose?: () => void
  /** Error from the last save attempt (cycle, Supabase failure); syntax errors are local. */
  saveError?: string | null
  busy?: boolean
  /** Shows the per-cell override policy for the selected calculated column. */
  showAllowCellOverrides?: boolean
  /** Controlled value of the per-cell override policy. Defaults to enabled for legacy configs. */
  allowCellOverrides?: boolean
  /** Called when the per-cell override policy changes. Omit to render a read-only control. */
  onAllowCellOverridesChange?: (allow: boolean) => void
  /** Columns of the formula a manual entry can be back-solved into (`goalSeek.ts`). Empty hides
   *  the selector: nothing in this formula can be solved for. */
  overrideTargetOptions?: FormulaTarget[]
  /** The field manual entries back-solve, or `SAME_COLUMN` ('') to leave the value in the cell. */
  overrideTarget?: string
  /** Omit to render the selector read-only. */
  onOverrideTargetChange?: (field: string) => void
  /** Column formula (default) or the active cell's own formula. In `cell` mode `formula`,
   *  `onAccept` and `onDelete` are about that cell; title and override controls are hidden. */
  mode?: FormulaBarMode
  /** Omit to hide the Column/Cell selector. */
  onModeChange?: (mode: FormulaBarMode) => void
  /** False when no cell is active or it cannot hold a formula; nothing can be accepted then. */
  cellAvailable?: boolean
}

function ModeBox({ mode, onChange }: { mode: FormulaBarMode; onChange: (mode: FormulaBarMode) => void }) {
  const { t } = useI18n()
  return (
    <select
      value={mode}
      onChange={(e) => onChange(e.currentTarget.value as FormulaBarMode)}
      title={t('grid.formulaModeTitle')}
      aria-label={t('grid.formulaModeTitle')}
      className="h-7 shrink-0 cursor-pointer rounded border border-border bg-bg px-1.5 text-xs outline-none focus:border-focus"
    >
      <option value="column">{t('grid.formulaModeColumn')}</option>
      <option value="cell">{t('grid.formulaModeCell')}</option>
    </select>
  )
}

// ── Shared dropdown primitive ─────────────────────────────────────────────────────────────────

interface Option {
  value: string
  label: string
  syntax?: string
  description?: string
}

/**
 * The small searchable list used by BOTH the name box and the reference autocomplete. It exists
 * because `CeldaDropdown` is a cell control — it always offers an "empty value" option and is
 * styled as a table cell, neither of which fits a toolbar or a caret-side list.
 *
 * Presentational on purpose: the highlighted index comes from the CONSUMER, which owns the
 * `useTecladoDesplegable` hook. The list cannot own it, because the keydown happens on the
 * consumer's input and this list is its sibling, not its ancestor — events would never reach a
 * handler placed here.
 */
function OptionList({
  options,
  activeIndex,
  activeRef,
  onPick,
  anchorRef,
  listId,
  optionId,
}: {
  options: Option[]
  activeIndex: number
  activeRef: (el: HTMLButtonElement | null) => void
  onPick: (value: string) => void
  anchorRef: RefObject<HTMLElement | null>
  listId: string
  optionId: (i: number) => string
}) {
  const { t } = useI18n()
  const [style, setStyle] = useState<CSSProperties | null>(null)

  useLayoutEffect(() => {
    const position = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const margin = 8
      const gap = 4
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - margin * 2)
      const left = Math.min(Math.max(rect.left, margin), window.innerWidth - width - margin)
      const spaceBelow = window.innerHeight - rect.bottom - margin - gap
      const spaceAbove = rect.top - margin - gap
      const expectedHeight = Math.min(256, Math.max(36, options.length * 36 + 8))
      const openAbove = spaceBelow < expectedHeight && spaceAbove > spaceBelow
      const maxHeight = Math.max(80, Math.min(256, openAbove ? spaceAbove : spaceBelow))
      setStyle(openAbove
        ? { position: 'fixed', left, bottom: window.innerHeight - rect.top + gap, width, maxHeight, zIndex: 100 }
        : { position: 'fixed', left, top: rect.bottom + gap, width, maxHeight, zIndex: 100 })
    }

    position()
    window.addEventListener('resize', position)
    window.addEventListener('scroll', position, true)
    return () => {
      window.removeEventListener('resize', position)
      window.removeEventListener('scroll', position, true)
    }
  }, [anchorRef, options.length])

  if (!style) return null

  return createPortal(
    <div
      id={listId}
      role="listbox"
      aria-label={t('grid.formulaColumnOptions')}
      style={style}
      className="overflow-y-auto rounded-lg border border-border bg-surface py-1 text-xs shadow-card"
    >
      {options.length === 0 ? (
        <p className="px-2 py-1.5 text-text-muted">{t('grid.noMatches')}</p>
      ) : (
        options.map((option, i) => (
          <button
            key={option.value}
            type="button"
            ref={i === activeIndex ? activeRef : undefined}
            id={optionId(i)}
            role="option"
            aria-selected={i === activeIndex}
            // Keep the caret in the editor: losing focus would close the list before the click.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(option.value)}
            className={`block w-full px-2.5 py-1.5 text-left transition-colors ${
              i === activeIndex ? 'bg-brand/10 text-brand' : 'hover:bg-black/5 dark:hover:bg-white/10 text-fg'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono truncate">{option.label}</span>
              {option.syntax && <span className="font-mono text-[11px] opacity-70 truncate">{option.syntax}</span>}
            </div>
            {option.description && (
              <p className="mt-0.5 text-[10px] text-text-muted line-clamp-2">{option.description}</p>
            )}
          </button>
        ))
      )}
    </div>,
    document.body,
  )
}

// ── Name box ──────────────────────────────────────────────────────────────────────────────────

/** The column selector of the formula bar and the format bar (Excel's name box). */
export function NameBox({
  targets,
  selected,
  onSelect,
  resizable = false,
  allowNew = true,
  disabled = false,
  selectionCount = 1,
}: {
  targets: FormulaTarget[]
  selected: string
  onSelect: (field: string) => void
  resizable?: boolean
  /** Offers "New calculated column" at the end of the list (formula bar only). */
  allowNew?: boolean
  disabled?: boolean
  /** Multiple selected columns in the data/alignment toolbar; formulas stay singular. */
  selectionCount?: number
}) {
  const { t } = useI18n()
  const listId = useId()
  const optionId = (i: number) => `${listId}-option-${i}`
  const containerRef = useRef<HTMLDivElement>(null)
  const label =
    selectionCount > 1
      ? t('grid.selectedColumnCount', { count: selectionCount })
      : selected === NEW_COLUMN
        ? t('grid.formulaNewColumn')
        : (targets.find((target) => target.field === selected)?.name ?? t('grid.formulaPickColumn'))
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  /** Width the person dragged the box to; null fits it to the longest column name. */
  const [customWidth, setWidth] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem('numtable_formulabar_namebox_width')
      if (saved) {
        const val = Number(saved)
        if (Number.isFinite(val) && val >= 80 && val <= 600) return val
      }
    } catch {}
    return null
  })
  const [fitWidth, setFitWidth] = useState(176)
  const width = customWidth ?? fitWidth
  const labelsKey = JSON.stringify([...targets.map((target) => (target.calculated ? `ƒ ${target.name}` : target.name)), ...(selectionCount > 1 ? [label] : [])])
  // Measured in the box's own font, like Excel's name box.
  useLayoutEffect(() => {
    const box = containerRef.current
    const labels = JSON.parse(labelsKey) as string[]
    if (!resizable || !box || labels.length === 0) return
    const context = document.createElement('canvas').getContext('2d')
    if (!context) return
    const style = getComputedStyle(box.querySelector('button') ?? box)
    context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    const longest = Math.max(...labels.map((text) => context.measureText(text).width))
    // Side padding, the gap and the ▾ arrow.
    setFitWidth(Math.max(80, Math.min(600, Math.ceil(longest + 40))))
  }, [labelsKey, resizable])

  const dragRef = useRef<{ x0: number; w0: number } | null>(null)

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { x0: e.clientX, w0: width }

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragRef.current) return
      const delta = ev.clientX - dragRef.current.x0
      const next = Math.max(80, Math.min(600, dragRef.current.w0 + delta))
      setWidth(next)
    }

    const onPointerUp = (ev: PointerEvent) => {
      if (dragRef.current) {
        const delta = ev.clientX - dragRef.current.x0
        const next = Math.max(80, Math.min(600, dragRef.current.w0 + delta))
        setWidth(next)
        try {
          localStorage.setItem('numtable_formulabar_namebox_width', String(next))
        } catch {}
        dragRef.current = null
      }
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  const options = useMemo<Option[]>(() => {
    const q = query.trim().toLocaleLowerCase('es')
    const matches = targets
      .filter((target) => target.name.toLocaleLowerCase('es').includes(q))
      .map((target) => ({ value: target.field, label: target.calculated ? `ƒ ${target.name}` : target.name }))
    return allowNew ? [...matches, { value: NEW_COLUMN, label: t('grid.formulaNewCalculatedColumn') }] : matches
  }, [allowNew, query, targets, t])

  const close = () => {
    setOpen(false)
    setQuery('')
  }

  const pick = (value: string) => {
    onSelect(value)
    close()
  }

  // Same reason as in the editor: the keydown happens on the search input, and the list is its
  // sibling, so the dropdown keyboard has to live here.
  const keyboard = useTecladoDesplegable<HTMLButtonElement>({
    cantidad: options.length,
    abierto: open,
    indiceInicial: 0,
    reiniciarCon: query,
    elegir: (i) => {
      const option = options[i]
      if (option) pick(option.value)
    },
    cerrar: close,
  })

  return (
    <div
      ref={containerRef}
      style={{ width: resizable ? `${width + 10}px` : undefined }}
      className={`relative shrink-0 ${resizable ? 'pr-2.5' : 'w-full'}`}
    >
      {open ? (
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={keyboard.alTeclear}
          onBlur={close}
          placeholder={t('grid.formulaSearchColumn')}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={optionId(keyboard.indice)}
          className="h-7 w-full rounded-full border border-focus bg-bg px-2.5 text-xs outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          title={t('grid.formulaColumnBeingEdited')}
          className={`flex h-7 max-w-full items-center gap-1 rounded px-2.5 text-xs font-normal ${resizable ? 'w-full justify-between' : 'justify-start'} transition-colors hover:bg-surface-hover disabled:opacity-50`}
        >
          <span className="truncate">{label}</span>
          <Caret />
        </button>
      )}
      {resizable && (
        <div
          onPointerDown={startResize}
          {...doubleTapProps(() => {
            setWidth(null)
            try {
              localStorage.removeItem('numtable_formulabar_namebox_width')
            } catch {}
          })}
          className="group/namebox-resize absolute right-0 top-0 bottom-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center select-none touch-none"
          title={t('grid.dragResize')}
        >
          <span aria-hidden="true" className="h-5 w-px bg-border transition-colors group-hover/namebox-resize:w-0.5 group-hover/namebox-resize:bg-accent group-active/namebox-resize:w-0.5 group-active/namebox-resize:bg-accent" />
        </div>
      )}
      {open && (
        <OptionList options={options} activeIndex={keyboard.indice} activeRef={keyboard.refActiva} onPick={pick} anchorRef={containerRef} listId={listId} optionId={optionId} />
      )}
    </div>
  )
}

/**
 * Which column a manual entry back-solves. Sits beside the «Allow overrides» checkbox and only
 * while it is ticked, because back-solving IS an override — just one that lands elsewhere.
 *
 * No search box: the list is the columns of one formula, never long enough to need filtering.
 * The dropdown keyboard therefore hangs off the button itself.
 */
function OverrideTargetBox({
  options,
  selected,
  onSelect,
  disabled,
}: {
  options: FormulaTarget[]
  selected: string
  onSelect: (field: string) => void
  disabled: boolean
}) {
  const { t } = useI18n()
  const listId = useId()
  const optionId = (i: number) => `${listId}-option-${i}`
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const entries = useMemo<Option[]>(
    () => [
      { value: SAME_COLUMN, label: t('grid.formulaOverrideTargetSame') },
      ...options.map((option) => ({ value: option.field, label: option.name })),
    ],
    [options, t],
  )

  const label = entries.find((entry) => entry.value === selected)?.label ?? t('grid.formulaOverrideTargetSame')

  const pick = (value: string) => {
    setOpen(false)
    if (value !== selected) onSelect(value)
  }

  const keyboard = useTecladoDesplegable<HTMLButtonElement>({
    cantidad: entries.length,
    abierto: open,
    indiceInicial: Math.max(0, entries.findIndex((entry) => entry.value === selected)),
    elegir: (i) => {
      const entry = entries[i]
      if (entry) pick(entry.value)
    },
    cerrar: () => setOpen(false),
    abrir: () => setOpen(true),
  })

  return (
    <div ref={containerRef} className="relative min-w-0 shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={keyboard.alTeclear}
        onBlur={() => setOpen(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t('grid.formulaOverrideTarget')}
        title={t('grid.formulaOverrideTargetTitle')}
        className="flex h-7 max-w-44 items-center justify-between gap-1 rounded px-2.5 text-xs transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
      >
        <span className="truncate">{label}</span>
        <Caret />
      </button>
      {open && (
        <OptionList
          options={entries}
          activeIndex={keyboard.indice}
          activeRef={keyboard.refActiva}
          onPick={pick}
          anchorRef={containerRef}
          listId={listId}
          optionId={optionId}
        />
      )}
    </div>
  )
}

// ── Bar ───────────────────────────────────────────────────────────────────────────────────────

export default function FormulaBar({
  targets,
  referenceTargets,
  selected,
  onSelectTarget,
  formula,
  completionSchema,
  title,
  scope,
  locale,
  onAccept,
  onDelete,
  insertRequest,
  onPointRef,
  pointRequest,
  operandRefs,
  expanded,
  onToggleExpanded,
  onClose,
  saveError,
  busy = false,
  showAllowCellOverrides = false,
  allowCellOverrides = true,
  onAllowCellOverridesChange,
  overrideTargetOptions = [],
  overrideTarget = SAME_COLUMN,
  onOverrideTargetChange,
  mode = 'column',
  onModeChange,
  cellAvailable = true,
}: FormulaBarProps) {
  const { t } = useI18n()
  const cellMode = mode === 'cell'
  const creating = !cellMode && selected === NEW_COLUMN
  const selectedTarget = targets.find((target) => target.field === selected)
  // UI-locale-dependent (English/Spanish/Portuguese function help text), independent of `locale`
  // (which is only the formula's decimal/argument separator).
  const functionMetadata = useMemo(() => localizedFunctionMetadata(t), [t])

  // What the user reads and edits: visible names and their own decimal separator. Recomputed
  // from the stored formula whenever the selected column changes. `fromStored` always yields a
  // node — a `raw` one for anything unreadable, which serializes back to its own text — so a
  // formula that does not parse stays editable instead of disappearing. That is the only way the
  // user can repair it.
  const displayed = useMemo(
    () => (formula === null ? '' : `=${serialize(fromStored(formula), scope, locale)}`),
    [formula, locale, scope],
  )

  const [draft, setDraft] = useState(displayed)
  const [draftTitle, setDraftTitle] = useState(title)
  const [dirty, setDirty] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  // While the caret sits inside an in-progress `[reference` or function name, the editor is
  // still typing something incomplete-but-not-wrong yet — don't yell about it.
  const [activeTyping, setActiveTyping] = useState(false)

  const isEditing = isFocused || dirty || creating

  // A formula on the clipboard (text starting with `=`) offers a paste button. Reading the
  // clipboard silently needs the permission already granted; otherwise only what this bar copied
  // is known, and the paste click itself reads the clipboard.
  const [clipboardFormula, setClipboardFormula] = useState<string | null>(lastCopiedFormula)
  useEffect(() => {
    const check = async () => {
      try {
        const status = await navigator.permissions?.query({ name: 'clipboard-read' as PermissionName })
        if (status?.state !== 'granted') return
        const text = (await navigator.clipboard.readText()).trim()
        setClipboardFormula(text.startsWith('=') ? text : null)
      } catch { /* unsupported: keep what we know */ }
    }
    void check()
    window.addEventListener('focus', check)
    return () => window.removeEventListener('focus', check)
  }, [])

  // Reset when the bar points somewhere else, but never stomp on a formula being written.
  useEffect(() => {
    setDraft(displayed)
    setDraftTitle(title)
    setDirty(false)
    setIsFocused(false)
  }, [displayed, selected, title, mode])

  // Shown with a leading `=`, as in the cell editor; typing it is optional.
  const body = draft.trimStart().startsWith('=') ? draft.trimStart().slice(1) : draft
  const parsed = useMemo(() => (body.trim() === '' ? null : parseFormula(body, scope, locale)), [body, locale, scope])
  const syntaxError = dirty && parsed !== null && !parsed.ok ? diagnosticText(t, parsed.error) : null
  // Accepted, but worth saying — a stray dot before `[`, which saving normalises away. Never
  // shown at the same time as an error, since only one of the two can exist.
  const warning = parsed !== null && parsed.ok && parsed.warnings.length > 0
    ? diagnosticText(t, parsed.warnings[0]!)
    : null
  const hasTitle = !creating || draftTitle.trim() !== ''
  const canAccept = !busy && !(cellMode && !cellAvailable) && parsed !== null && parsed.ok && hasTitle

  const accept = () => {
    if (!canAccept || parsed === null || !parsed.ok) return
    onAccept(toStored(parsed.ast), normalizeText(draftTitle))
    setDirty(false)
    setIsFocused(false)
  }

  /**
   * Saving a draft that does NOT parse, on purpose. It is kept verbatim as a `raw` formula, so
   * leaving the bar mid-thought does not throw the work away; the column evaluates to an error
   * until it is repaired, which is honest about the state it is in.
   */
  const acceptBroken = () => {
    if (busy || (cellMode && !cellAvailable) || parsed === null || parsed.ok || !hasTitle) return
    onAccept(rawFormula(body), normalizeText(draftTitle))
    setDirty(false)
    setIsFocused(false)
  }

  const cancel = () => {
    setDraft(displayed)
    setDraftTitle(title)
    setDirty(false)
    setIsFocused(false)
  }

  /** Height the person dragged the inline editor to; null keeps the one-line bar. */
  const [barHeight, setBarHeight] = useState<number | null>(() => {
    try {
      const val = Number(localStorage.getItem(BAR_HEIGHT_KEY))
      if (Number.isFinite(val) && val > BAR_MIN_HEIGHT && val <= BAR_MAX_HEIGHT) return val
    } catch {}
    return null
  })
  const tall = !expanded && barHeight !== null

  const startHeightResize = (e: React.PointerEvent) => {
    e.preventDefault()
    const y0 = e.clientY
    const h0 = barHeight ?? BAR_MIN_HEIGHT
    const heightAt = (y: number) => Math.max(BAR_MIN_HEIGHT, Math.min(BAR_MAX_HEIGHT, h0 + y - y0))
    const onPointerMove = (ev: PointerEvent) => {
      const next = heightAt(ev.clientY)
      setBarHeight(next > BAR_MIN_HEIGHT ? next : null)
    }
    const onPointerUp = (ev: PointerEvent) => {
      const next = heightAt(ev.clientY)
      setBarHeight(next > BAR_MIN_HEIGHT ? next : null)
      try {
        if (next > BAR_MIN_HEIGHT) localStorage.setItem(BAR_HEIGHT_KEY, String(next))
        else localStorage.removeItem(BAR_HEIGHT_KEY)
      } catch {}
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  const editorBoxRef = useRef<HTMLDivElement>(null)
  const editor = (
    <div
      ref={editorBoxRef}
      style={tall ? ({ '--formula-bar-height': `${barHeight}px` } as CSSProperties) : undefined}
      // A taller bar shows the whole formula wrapped, scrolling past its height; Enter still accepts.
      className={`relative min-h-0 min-w-[160px] flex-1 sm:min-w-[240px] ${tall ? '[&_.cm-editor]:!h-[var(--formula-bar-height)] [&_.cm-editor]:!max-h-none [&_.cm-editor]:!rounded-lg [&_.cm-content]:!whitespace-pre-wrap [&_.cm-scroller]:!overflow-auto' : ''}`}
    >
      <FormulaEditor
        value={draft}
        onChange={(text) => {
          setDraft(text)
          setDirty(true)
        }}
        onCommit={accept}
        onCancel={cancel}
        singleLine={!expanded}
        columns={referenceTargets ?? targets}
        schema={completionSchema}
        functionMetadata={functionMetadata}
        locale={locale}
        insertRequest={insertRequest}
        onPointRef={onPointRef}
        pointRequest={pointRequest}
        operandRefs={operandRefs}
        autoFocus={expanded}
        syntaxError={syntaxError}
        placeholder={cellMode && !cellAvailable ? t('grid.formulaCellUnavailable') : t('grid.formulaPlaceholder')}
        onActiveTypingChange={setActiveTyping}
        onFocusChange={setIsFocused}
      />
      {!expanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          title={t('grid.formulaExpand')}
          aria-label={t('grid.formulaExpand')}
          className="absolute right-1 top-0.5 flex h-6 w-6 items-center justify-center rounded text-xs text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
        >
          ⤢
        </button>
      )}
      {/* No error while a reference/function is still being written: typing alone is not a mistake yet */}
      {syntaxError && !activeTyping && (
        <FormulaBarTooltip anchor={editorBoxRef} role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-danger bg-surface px-2 py-1 text-[11px] font-bold text-danger shadow-lg">
          <span>{syntaxError}</span>
          <button
            type="button"
            onClick={acceptBroken}
            className="rounded-full border border-danger px-2 py-0.5 font-normal"
          >
            {t('grid.formulaSaveAnyway')}
          </button>
        </FormulaBarTooltip>
      )}
      {/* A warning never blocks: the formula parsed, and saving writes the canonical form back. */}
      {warning && !activeTyping && !syntaxError && (
        <FormulaBarTooltip anchor={editorBoxRef} role="status" className="rounded-md border border-warning bg-surface px-2 py-1 text-[11px] text-warning shadow-lg">{warning}</FormulaBarTooltip>
      )}
    </div>
  )

  const titleInput = !cellMode && (creating || selectedTarget?.calculated) && (
    <input
      value={draftTitle}
      onChange={(e) => {
        setDraftTitle(e.target.value)
        setDirty(true)
      }}
      placeholder="Nombre de la columna"
      className="h-7 w-40 shrink-0 rounded-full border border-border bg-bg px-2.5 text-xs outline-none focus:border-focus"
    />
  )

  const allowOverridesControl = !cellMode && showAllowCellOverrides
    && selectedTarget !== undefined
    && formula !== null && (
    <div className="flex min-w-0 shrink-0 items-center gap-1.5">
      <label className="flex h-7 max-w-48 min-w-0 items-center gap-1.5 overflow-hidden rounded px-2 text-xs text-fg transition-colors hover:bg-surface-hover">
        <input
          type="checkbox"
          checked={allowCellOverrides}
          disabled={busy || onAllowCellOverridesChange === undefined}
          onChange={(event) => onAllowCellOverridesChange?.(event.currentTarget.checked)}
          aria-label={t('grid.formulaAllowCellOverrides')}
          className="h-3.5 w-3.5 shrink-0 accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed"
        />
        <span className="truncate">{t('grid.formulaAllowCellOverrides')}</span>
      </label>
      {/* Only while overrides are on: with them suspended there is no manual entry to redirect. */}
      {allowCellOverrides && overrideTargetOptions.length > 0 && (
        <OverrideTargetBox
          options={overrideTargetOptions}
          selected={overrideTarget}
          onSelect={(field) => onOverrideTargetChange?.(field)}
          disabled={busy || onOverrideTargetChange === undefined}
        />
      )}
    </div>
  )

  // Only while actually editing: focused or holding unsaved changes. `creating` alone (the
  // new-column target selected but untouched) is not editing.
  const copyFormula = () => {
    if (!displayed) return
    lastCopiedFormula = displayed
    setClipboardFormula(displayed)
    void navigator.clipboard?.writeText(displayed).catch(() => {})
  }
  const pasteFormula = async () => {
    let text = clipboardFormula
    try {
      const read = (await navigator.clipboard?.readText())?.trim()
      if (read !== undefined) text = read
    } catch { /* clipboard unreadable: keep what we copied ourselves */ }
    if (!text?.startsWith('=')) { setClipboardFormula(null); return }
    setDraft(text)
    setDirty(true)
  }

  const clipboardActions = !(isFocused || dirty) && (displayed || (clipboardFormula && clipboardFormula !== displayed)) && (
    <div className="flex shrink-0 items-center gap-0.5">
      {displayed && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={copyFormula}
          title={t('grid.formulaCopy')}
          aria-label={t('grid.formulaCopy')}
          className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></svg>
        </button>
      )}
      {clipboardFormula && clipboardFormula !== displayed && !busy && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { void pasteFormula() }}
          title={t('grid.formulaPaste')}
          aria-label={t('grid.formulaPaste')}
          className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /></svg>
        </button>
      )}
    </div>
  )

  const editActions = (isFocused || dirty) && (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        // Cancel and leave the editor, expanded or not, so editing mode ends.
        onClick={() => { cancel(); (document.activeElement as HTMLElement | null)?.blur() }}
        disabled={busy}
        title={t('grid.cancel')}
        className="flex h-7 w-7 items-center justify-center rounded text-xs font-bold transition-colors hover:bg-surface-hover disabled:opacity-40"
      >
        ✕
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        // Commit and leave the editor, like ✕, so editing mode ends.
        onClick={() => { accept(); (document.activeElement as HTMLElement | null)?.blur() }}
        disabled={!canAccept}
        title={t('grid.accept')}
        className="flex h-7 w-7 items-center justify-center rounded text-xs font-bold text-success transition-colors hover:bg-success/10 disabled:opacity-40"
      >
        ✓
      </button>
    </div>
  )

  const deleteLabel = cellMode
    ? t('grid.formulaDeleteCell')
    : selectedTarget?.calculated ? t('grid.formulaDeleteColumn') : t('grid.formulaDeleteOverride')
  const modeBox = onModeChange && <ModeBox mode={mode} onChange={onModeChange} />

  const deleteButton = onDelete && !creating && formula !== null && (
    <button
      type="button"
      onClick={onDelete}
      disabled={busy}
      title={deleteLabel}
      aria-label={deleteLabel}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-xs font-bold text-danger transition-colors hover:bg-danger-soft disabled:opacity-40"
    >
      🗑
    </button>
  )

  // Expanded: the side panel owns the layout, stacking the pieces in full height.
  if (expanded) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1">
            <NameBox targets={targets} selected={selected} onSelect={onSelectTarget} />
          </div>
          {modeBox}
          <button
            type="button"
            onClick={onToggleExpanded}
            title={t('grid.formulaCollapseTitle')}
            aria-label={t('grid.formulaCollapse')}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-xs transition-colors hover:bg-surface-hover"
          >
            ⤡
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              title={t('grid.formulaClose')}
              aria-label={t('grid.formulaClose')}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-sm transition-colors hover:bg-surface-hover"
            >
              ✕
            </button>
          )}
        </div>
        {titleInput}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {editor}
          {(editActions || deleteButton) && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1">
              {editActions}
              {deleteButton}
            </div>
          )}
        </div>
        {allowOverridesControl}
        {saveError && <p className="text-[11px] font-bold text-danger">{saveError}</p>}
      </div>
    )
  }

  // One line: what does not fit goes into a three-dots menu, as in the grid toolbar.
  const barItems: ToolbarItem[] = [
    { key: 'name', pinned: true, node: <NameBox targets={targets} selected={selected} onSelect={onSelectTarget} resizable /> },
    ...(modeBox ? [{ key: 'mode', pinned: true, node: modeBox }] : []),
    ...(titleInput ? [{ key: 'title', node: titleInput }] : []),
    ...(editActions ? [{ key: 'actions', node: editActions }] : []),
    ...(clipboardActions ? [{ key: 'clipboard', pinned: true, node: clipboardActions }] : []),
    { key: 'editor', node: editor, grow: true, minWidth: 160, pinned: true },
    ...(allowOverridesControl ? [{ key: 'overrides', node: allowOverridesControl }] : []),
    ...(deleteButton ? [{
      key: 'delete',
      separatorBefore: Boolean(allowOverridesControl),
      node: deleteButton,
      menu: [{
        icon: <span aria-hidden="true">🗑</span>,
        label: deleteLabel,
        onSelect: () => onDelete?.(),
      }],
    }] : []),
  ]

  return (
    <div className="mb-0.5 flex shrink-0 flex-col gap-1">
      <ToolbarOverflow items={barItems} menuLabel={t('grid.moreOptions')} align="start" measureKey={`${selected}:${allowCellOverrides}:${mode}`} />
      {/* Drag down to make the bar taller; double-tap to go back to one line. */}
      <div
        onPointerDown={startHeightResize}
        {...doubleTapProps(() => {
          setBarHeight(null)
          try {
            localStorage.removeItem(BAR_HEIGHT_KEY)
          } catch {}
        })}
        title={t('grid.dragResize')}
        className="group/bar-resize -mt-1 flex h-1.5 cursor-row-resize items-center justify-center select-none touch-none"
      >
        <span aria-hidden="true" className="h-px w-10 bg-border transition-colors group-hover/bar-resize:h-0.5 group-hover/bar-resize:bg-accent group-active/bar-resize:h-0.5 group-active/bar-resize:bg-accent" />
      </div>
      {saveError && <p className="text-[11px] font-bold text-danger">{saveError}</p>}
    </div>
  )
}

/**
 * An error or warning floating above the formula input. Portaled to `body` with fixed
 * coordinates so no toolbar row or sticky header can paint over it.
 */
function FormulaBarTooltip({ anchor, role, className, children }: {
  anchor: RefObject<HTMLDivElement | null>
  role: 'alert' | 'status'
  className: string
  children: React.ReactNode
}) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useLayoutEffect(() => {
    const update = () => setRect(anchor.current?.getBoundingClientRect() ?? null)
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])
  if (rect === null) return null
  const style: CSSProperties = {
    position: 'fixed',
    left: rect.left + 8,
    bottom: window.innerHeight - rect.top + 6,
    maxWidth: rect.width - 16,
  }
  return createPortal(
    <div role={role} style={style} className={`z-[1000] ${className}`}>{children}</div>,
    document.body,
  )
}
